import { FC, ReactNode, useEffect, useMemo, useRef } from "react";
import {
  createClient,
  createLDReactProviderWithClient,
  LDContext,
  LDReactClient,
} from "@launchdarkly/react-sdk";
import { useAppContext } from "../../../AppContext";
import { createDisabledFeatureFlagClient } from "../helpers/disabledFeatureFlagClientHelper";
import { buildFeatureFlagContext } from "../helpers/featureFlagContextHelper";
import { exposeFeatureFlagsOnWindow } from "../helpers/featureFlagWindowHelper";

interface IFeatureFlags {
  client: LDReactClient;
  Provider: FC<{ children: ReactNode }>;
  isEnabled: boolean;
}

// One LaunchDarkly client per page load — it owns the flag cache and the streaming connection.
let featureFlags: IFeatureFlags | undefined;

const createLaunchDarklyClient = (initialContext: LDContext) => {
  const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID?.trim() ?? "";
  if (!clientSideId) {
    return undefined;
  }

  try {
    const client = createClient(clientSideId, initialContext);
    client.start();
    return client;
  } catch {
    // The SDK reads localStorage while creating its client, which throws when the browser
    // blocks site data. Flags are optional — never let them take the app down.
    return undefined;
  }
};

const getFeatureFlags = (initialContext: LDContext): IFeatureFlags => {
  if (!featureFlags) {
    const launchDarklyClient = createLaunchDarklyClient(initialContext);
    const client = launchDarklyClient ?? createDisabledFeatureFlagClient();
    exposeFeatureFlagsOnWindow(client);

    featureFlags = {
      client,
      Provider: createLDReactProviderWithClient(client),
      isEnabled: !!launchDarklyClient,
    };
  }

  return featureFlags;
};

export const useFeatureFlagsProvider = () => {
  const { currentUser, selectedStudioId } = useAppContext();
  const userId = currentUser?.id;
  const userRole = currentUser?.role;

  const context = useMemo(
    () => buildFeatureFlagContext(userId ? { id: userId, role: userRole } : null, selectedStudioId),
    [userId, userRole, selectedStudioId]
  );

  const { client, Provider, isEnabled } = getFeatureFlags(context);

  // The client starts with the context from the first render; re-identify only on real changes
  // (login, logout, studio switch).
  const identifiedContextRef = useRef(JSON.stringify(context));

  useEffect(() => {
    const serializedContext = JSON.stringify(context);
    if (!isEnabled || identifiedContextRef.current === serializedContext) {
      return;
    }

    identifiedContextRef.current = serializedContext;
    client.identify(context);
  }, [client, context, isEnabled]);

  return Provider;
};
